/**
 * OddsWithMovement
 * Shows odds value and a brief up/down arrow when odds change (up = increased, down = decreased).
 * Arrow is shown for 2.5 seconds after a change.
 */

import React, { useEffect, useRef, useState } from 'react';
import { formatOdds } from '../../services/sportsService';

const ARROW_DURATION_MS = 2500;

export interface OddsWithMovementProps {
  /** Stable key for this cell (e.g. fixtureId-betId) so we can track previous value */
  cellKey: string;
  /** Current odds value; null/undefined = show '-' and no arrow */
  odds: number | null | undefined;
  /** Optional class for the wrapper span */
  className?: string;
  /** When true, show lock instead of odds (suspended) */
  suspended?: boolean;
  /** When true, hide odds and show lock (e.g. odds below minimum) */
  locked?: boolean;
}

const OddsWithMovement: React.FC<OddsWithMovementProps> = ({
  cellKey,
  odds,
  className = '',
  suspended = false,
  locked = false,
}) => {
  const prevOddsRef = useRef<Record<string, number>>({});
  const [direction, setDirection] = useState<'up' | 'down' | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (suspended || locked || odds === null || odds === undefined || typeof odds !== 'number' || !Number.isFinite(odds)) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setDirection(null);
      return;
    }

    const prev = prevOddsRef.current[cellKey];
    if (prev !== undefined && prev !== odds) {
      setDirection(odds > prev ? 'up' : 'down');
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setDirection(null);
        timeoutRef.current = null;
      }, ARROW_DURATION_MS);
    }
    prevOddsRef.current[cellKey] = odds;

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [cellKey, odds, suspended, locked]);

  if (suspended || locked) {
    return <span className={className}>🔒</span>;
  }

  if (odds === null || odds === undefined || typeof odds !== 'number' || !Number.isFinite(odds)) {
    return <span className={className}>-</span>;
  }

  return (
    <span className={`inline-flex items-center gap-0.5 ${className}`}>
      {direction === 'up' && (
        <span className="text-green-400" aria-hidden>
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </span>
      )}
      {direction === 'down' && (
        <span className="text-red-400" aria-hidden>
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </span>
      )}
      {formatOdds(odds)}
    </span>
  );
};

export default OddsWithMovement;
